import { CallingHolidayRule } from "@/enum/CallingHolidayRule";
import { CallingRecordedMessages } from "@/enum/CallingRecordedMessages";
import { CallingRuleBasis } from "@/enum/CallingRuleBasis";
import type { CallingWeek, CallingWeekDay, CallingWindow } from "@/model/interface/CallingRule";

export const FEDERAL_REGION = "US";

export const CALLING_WEEK_DAYS: { day: CallingWeekDay; label: string; shortLabel: string }[] = [
  { day: "monday", label: "Monday", shortLabel: "Mon" },
  { day: "tuesday", label: "Tuesday", shortLabel: "Tue" },
  { day: "wednesday", label: "Wednesday", shortLabel: "Wed" },
  { day: "thursday", label: "Thursday", shortLabel: "Thu" },
  { day: "friday", label: "Friday", shortLabel: "Fri" },
  { day: "saturday", label: "Saturday", shortLabel: "Sat" },
  { day: "sunday", label: "Sunday", shortLabel: "Sun" },
];

export const HOLIDAY_RULE_LABELS: Record<CallingHolidayRule, string> = {
  [CallingHolidayRule.NONE]: "Calls allowed on holidays",
  [CallingHolidayRule.FEDERAL]: "No calls on federal holidays",
  [CallingHolidayRule.STATE]: "No calls on state holidays",
  [CallingHolidayRule.STATE_AND_FEDERAL]: "No calls on state or federal holidays",
};

export const RECORDED_MESSAGES_LABELS: Record<CallingRecordedMessages, string> = {
  [CallingRecordedMessages.WRITTEN_CONSENT]: "With written consent",
  [CallingRecordedMessages.NEVER]: "Never",
  [CallingRecordedMessages.NEEDS_REVIEW]: "Needs review",
};

export const RULE_BASIS_LABELS: Record<CallingRuleBasis, string> = {
  [CallingRuleBasis.STATUTE_TEXT]: "Rule text",
  [CallingRuleBasis.LEGAL_SUMMARY]: "Legal summary",
  [CallingRuleBasis.NEEDS_REVIEW]: "Needs review",
};

export function formatCallingTime(value: string): string {
  const [hourText, minuteText] = value.split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return value;
  }
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0 ? `${displayHour} ${suffix}` : `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function describeWindow(dayWindow: CallingWindow | null | undefined): string {
  if (!dayWindow) {
    return "no calls";
  }
  return `${formatCallingTime(dayWindow.start)} to ${formatCallingTime(dayWindow.end)}`;
}

export function summarizeWeek(week: CallingWeek): string {
  const groups: { first: string; last: string; text: string }[] = [];
  CALLING_WEEK_DAYS.forEach(({ day, shortLabel }) => {
    const text = describeWindow(week[day]);
    const previous = groups[groups.length - 1];
    if (previous && previous.text === text) {
      previous.last = shortLabel;
      return;
    }
    groups.push({ first: shortLabel, last: shortLabel, text });
  });
  if (groups.length === 1) {
    return `Every day ${groups[0].text}`;
  }
  return groups
    .map((group) => (group.first === group.last ? `${group.first} ${group.text}` : `${group.first} to ${group.last} ${group.text}`))
    .join(" · ");
}
